// =====================================================
import { getWorldGoalFamily, getWorldSignFlavor } from '../engine/worldIdentityV2';
// Zodiac Crush – Level Goals (PURE MODULE)
// =====================================================
// Este archivo:
// - Define los objetivos por nivel
// - Maneja progreso del nivel
// - NO usa React
// - NO tiene efectos secundarios
// - Es 100% reusable (screen, map, world, etc.)
// =====================================================

// Tipos de objetivos soportados (core):
// - score
// - stripesActivated
// - starsCreated
// - cascades
// - clear_element_fire
// - clear_element_water
// - clear_element_air
// - clear_element_earth

// -----------------------------------------------------
// Goal type metadata (single source of truth)
// -----------------------------------------------------
const GOAL_META = {
  score: {
    label: 'Sumá puntos',
    icon: '🎯',
    // For score, progress is the ABSOLUTE score (not a delta)
    mode: 'absolute',
  },
  stripesActivated: {
    label: 'Activá rayos',
    icon: '⚡',
    // For activations/clears, progress is accumulated per move
    mode: 'delta',
    deltaKey: 'stripesActivated',
  },
  starsCreated: {
    label: 'Creá estrellas',
    icon: '⭐',
    mode: 'delta',
    deltaKey: 'starsCreated',
  },
  cascades: {
    label: 'Nivel: Lográ cascadas',
    icon: '🌊',
    // Cascades are usually tracked as a RUN metric (max cascade depth) in the engine.
    // Make this goal resilient by accepting either:
    // - moveAgg.maxCascade (absolute, typical)
    // - moveAgg.cascades (some builds use this)
    // - moveAgg.maxCascadeDepth (alternate naming)
    mode: 'absolute',
    // Accept many naming variants (camelCase + snake_case) coming from different reporters/builds.
    absKeys: [
      'maxCascade',
      'max_cascade',
      'maxCascades',
      'max_cascades',
      'cascades',
      'cascadesReach',
      'cascades_reach',
      'maxCascadeDepth',
      'max_cascade_depth',
      'cascadeDepth',
      'cascade_depth',
      'bestCascade',
      'best_cascade',
      'cascadesBest',
      'cascades_best',
    ],
  },
  // Back-compat: some screens/builds use `cascades_reach` for the same goal.
  cascades_reach: {
    label: 'Nivel: Lográ cascadas',
    icon: '🌊',
    mode: 'absolute',
    // Accept many naming variants (camelCase + snake_case) coming from different reporters/builds.
    absKeys: [
      'maxCascade',
      'max_cascade',
      'maxCascades',
      'max_cascades',
      'cascades',
      'cascadesReach',
      'cascades_reach',
      'maxCascadeDepth',
      'max_cascade_depth',
      'cascadeDepth',
      'cascade_depth',
      'bestCascade',
      'best_cascade',
      'cascadesBest',
      'cascades_best',
    ],
  },
  clear_element_fire: {
    label: 'Limpiá fuego',
    icon: '🔥',
    mode: 'delta',
    // moveAgg uses clearedFire
    deltaKey: 'clearedFire',
  },
  clear_element_water: {
    label: 'Limpiá agua',
    icon: '💧',
    mode: 'delta',
    deltaKey: 'clearedWater',
  },
  clear_element_air: {
    label: 'Limpiá aire',
    icon: '💨',
    mode: 'delta',
    deltaKey: 'clearedAir',
  },
  clear_element_earth: {
    label: 'Limpiá tierra',
    icon: '🪨',
    mode: 'delta',
    deltaKey: 'clearedEarth',
  },
};

// -----------------------------------------------------
// Backend objective type aliases -> internal goal types
// -----------------------------------------------------
function normalizeGoalType(rawType) {
  const t = String(rawType || '').trim().toLowerCase();
  if (!t) return '';

  // Backend map/levels objectives (and common variants)
  if (t === 'reach_score' || t === 'score_reach' || t === 'score') return 'score';
  if (t === 'activate_stripe' || t === 'stripes_activated' || t === 'stripe' || t === 'stripes') return 'stripesActivated';
  if (t === 'make_star' || t === 'stars_created' || t === 'star' || t === 'stars') return 'starsCreated';

  // Cascades aliases
  if (t === 'cascades_reach' || t === 'reach_cascades' || t === 'max_cascade' || t === 'max_cascades' || t === 'cascade') return 'cascades';

  // Clear-element aliases (backend/meta may send short names)
  if (t === 'clear_fire' || t === 'clear_element_fire' || t === 'fire') return 'clear_element_fire';
  if (t === 'clear_water' || t === 'clear_element_water' || t === 'water') return 'clear_element_water';
  if (t === 'clear_air' || t === 'clear_element_air' || t === 'air') return 'clear_element_air';
  if (t === 'clear_earth' || t === 'clear_element_earth' || t === 'earth') return 'clear_element_earth';

  return t;
}

// -----------------------------------------------------
// Optional: read backend map-level objectives from the cached levels payload
// (Map screen/hook caches the 1..240 levels list under this key)
// This keeps UI goals consistent with the backend map tuning.
// -----------------------------------------------------
const ZC_LEVELS_CACHE_KEY = 'zc_map_levels_cache_v1';
const ZC_LEVELS_CACHE_SLIM_KEY = 'zc_map_levels_cache_slim_v1';

function readCachedLevelsAnyShape(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    const levels = Array.isArray(parsed?.levels)
      ? parsed.levels
      : (Array.isArray(parsed) ? parsed : null);
    return Array.isArray(levels) ? levels : null;
  } catch {
    return null;
  }
}

function tryGetCachedMapObjectives(absLevel) {
  try {
    if (typeof window === 'undefined') return null;
    const ls = window?.localStorage;
    if (!ls) return null;

    // Try FULL cache first, then SLIM cache.
    const rawFull = ls.getItem(ZC_LEVELS_CACHE_KEY) || '';
    const rawSlim = ls.getItem(ZC_LEVELS_CACHE_SLIM_KEY) || '';

    const levels =
      readCachedLevelsAnyShape(rawFull) ||
      readCachedLevelsAnyShape(rawSlim);

    if (!levels || !levels.length) return null;

    const lvlNum = Number(absLevel || 0) || 0;
    if (!lvlNum) return null;

    const hit = levels.find((x) => Number(x?.level || 0) === lvlNum);
    const objs = Array.isArray(hit?.objectives) ? hit.objectives : null;
    if (!objs || !objs.length) return null;

    // Return a shallow copy with normalized types
    // IMPORTANT: backend may send { type: 'clear_element', element: 'water' }
    // Map that to our internal types: clear_element_water / fire / air / earth.
    return objs
      .map((o) => {
        const rawType = String(o?.type || '').trim();
        const tNorm = normalizeGoalType(rawType);

        // Special-case: `clear_element` needs the element qualifier
        if (String(rawType || '').trim().toLowerCase() === 'clear_element') {
          let el = String(o?.element || '').trim().toLowerCase();
          try { el = el.normalize('NFD').replace(/[\u0300-\u036f]/g, ''); } catch (_) {}

          if (el === 'fire' || el === 'fuego') return { type: 'clear_element_fire', target: Number(o?.target || 0) || 0 };
          if (el === 'water' || el === 'agua') return { type: 'clear_element_water', target: Number(o?.target || 0) || 0 };
          if (el === 'air' || el === 'aire') return { type: 'clear_element_air', target: Number(o?.target || 0) || 0 };
          if (el === 'earth' || el === 'tierra') return { type: 'clear_element_earth', target: Number(o?.target || 0) || 0 };

          // If unknown element, fall back to score (never return a broken goal type)
          return { type: 'score', target: Number(o?.target || 0) || 0 };
        }

        return {
          type: tNorm,
          target: Number(o?.target || 0) || 0,
        };
      })
      .filter((o) => !!o.type && Number(o.target) > 0);
  } catch {
    return null;
  }
}

// -----------------------------------------------------
// World/level helpers (12 worlds × 20 levels = 240)
// -----------------------------------------------------
export const LEVELS_PER_WORLD = 20;
export const TOTAL_LEVELS = 240;

export const ZC_WORLD_ORDER = [
  'aries',
  'taurus',
  'gemini',
  'cancer',
  'leo',
  'virgo',
  'libra',
  'scorpio',
  'sagittarius',
  'capricorn',
  'aquarius',
  'pisces',
];

export const ZC_WORLD_ELEMENT = {
  aries: 'fire',
  leo: 'fire',
  sagittarius: 'fire',

  taurus: 'earth',
  virgo: 'earth',
  capricorn: 'earth',

  gemini: 'air',
  libra: 'air',
  aquarius: 'air',

  cancer: 'water',
  scorpio: 'water',
  pisces: 'water',
};
// -----------------------------------------------------
// Sign key normalization (front/back-compat)
// Backend / UI may use Spanish/Portuguese slugs (e.g. "geminis", "tauro").
// Normalize them to canonical keys used by `ZC_WORLD_ORDER`.
// -----------------------------------------------------
const ZC_SIGN_KEY_ALIASES = {
  // ES
  aries: 'aries',
  tauro: 'taurus',
  taurus: 'taurus',
  geminis: 'gemini',
  gemini: 'gemini',
  cancer: 'cancer',
  leo: 'leo',
  virgo: 'virgo',
  libra: 'libra',
  escorpio: 'scorpio',
  scorpio: 'scorpio',
  sagitario: 'sagittarius',
  sagittarius: 'sagittarius',
  capricornio: 'capricorn',
  capricorn: 'capricorn',
  acuario: 'aquarius',
  aquarius: 'aquarius',
  piscis: 'pisces',
  pisces: 'pisces',

  // PT (common)
  gemeos: 'gemini',
  leao: 'leo',
  escorpiao: 'scorpio',
  aquario: 'aquarius',
};

function normalizeWorldSignKey(raw) {
  let s = String(raw || '').trim().toLowerCase();
  if (!s) return '';
  try { s = s.normalize('NFD').replace(/[\u0300-\u036f]/g, ''); } catch (_) {}

  // remove separators/spaces -> keep letters/numbers
  s = s.replace(/[^a-z0-9]+/g, '');

  // map aliases
  return ZC_SIGN_KEY_ALIASES[s] || s;
}
// -----------------------------------------------------
// Level normalization
// Some callers may pass "level within world" (1..20) plus `signKey`.
// To keep overrides and calibration consistent, normalize to an ABSOLUTE level (1..240).
// If `level` already looks absolute (> 20), we keep it as-is.
// -----------------------------------------------------
export function normalizeAbsoluteLevel(level, signKey) {
  const lvRaw = Number(level || 1);
  const lv = Math.max(1, Math.floor(lvRaw));

  // If caller provided a valid signKey and level looks like "within world", map it to absolute.
  const sk = normalizeWorldSignKey(signKey);
    const wi = sk ? ZC_WORLD_ORDER.indexOf(sk) : -1;

  if (wi >= 0 && lv >= 1 && lv <= LEVELS_PER_WORLD) {
    const abs = wi * LEVELS_PER_WORLD + lv;
    return Math.max(1, Math.min(TOTAL_LEVELS, abs));
  }

  // Otherwise, treat `level` as already absolute (or unknown signKey)
  return Math.max(1, Math.min(TOTAL_LEVELS, lv));
}

export function getWorldIndexForLevel(level, signKey) {
  const lv = normalizeAbsoluteLevel(level, signKey);
  return Math.min(11, Math.floor((lv - 1) / LEVELS_PER_WORLD));
}

export function getIndexInWorld(level, signKey) {
  const lv = normalizeAbsoluteLevel(level, signKey);
  return ((lv - 1) % LEVELS_PER_WORLD) + 1; // 1..20
}

export function getWorldKeyForLevel(level, signKey) {
  return ZC_WORLD_ORDER[getWorldIndexForLevel(level, signKey)] || 'aries';
}

export function getWorldElementForLevel(level, signKey) {
  const k = getWorldKeyForLevel(level, signKey);
  return ZC_WORLD_ELEMENT[k] || null;
}

// -----------------------------------------------------
// Moves tuning (single source of truth)
// - early: 20 moves
// - mid:   28 moves
// - boss:  35 moves (nivel 20 de cada mundo)
// -----------------------------------------------------
export function getMovesForLevel(level, signKey) {
  const inWorld = getIndexInWorld(level, signKey);
  if (inWorld <= 7) return 20;     // early
  if (inWorld <= 19) return 28;    // mid
  return 35;                       // boss
}

function clamp(n, a, b) {
  const x = Number(n);
  if (!Number.isFinite(x)) return a;
  return Math.max(a, Math.min(b, x));
}

// -----------------------------------------------------
// Target calibration by moves bucket
// Goal: targets should feel achievable in ONE good run.
// Ranges requested:
// - Early (20 moves): score 200–350, clears 20–40
// - Mid   (28 moves): score 400–650, clears 50–80
// - Boss  (35 moves): score 800–1200, clears 90–130
// -----------------------------------------------------
function scoreTargetForLevel(level) {
  const lv = normalizeAbsoluteLevel(level);
  const wi = getWorldIndexForLevel(lv);
  const inWorld = getIndexInWorld(lv);
  const moves = getMovesForLevel(lv);

  // Small world ramp so later worlds feel slightly harder
  const worldRamp = wi * 12; // gentle

  if (moves === 20) {
    // 1..7
    const pos = clamp(inWorld, 1, 7) - 1;
    const base = 205 + worldRamp;
    const step = 22; // ~ +132 across early
    return clamp(base + pos * step, 200, 350);
  }

  if (moves === 28) {
    // 8..19
    const pos = clamp(inWorld, 8, 19) - 8; // 0..11
    const base = 410 + worldRamp;
    const step = 22; // ~ +242 across mid
    return clamp(base + pos * step, 400, 650);
  }

  // boss (20)
  const base = 860 + worldRamp * 2; // a bit more ramp on bosses
  return clamp(base, 800, 1200);
}

function clearsTargetForLevel(level) {
  const lv = normalizeAbsoluteLevel(level);
  const wi = getWorldIndexForLevel(lv);
  const inWorld = getIndexInWorld(lv);
  const moves = getMovesForLevel(lv);

  const worldRamp = wi * 2; // gentle

  if (moves === 20) {
    // 1..7
    const pos = clamp(inWorld, 1, 7) - 1;
    const base = 20 + worldRamp;
    const step = 3; // ~ +18 across early
    return clamp(base + pos * step, 20, 40);
  }

  if (moves === 28) {
    // 8..19
    const pos = clamp(inWorld, 8, 19) - 8; // 0..11
    const base = 50 + worldRamp;
    const step = 3; // ~ +33 across mid
    return clamp(base + pos * step, 50, 80);
  }

  // boss
  const base = 105 + worldRamp * 2;
  return clamp(base, 90, 130);
}

// -----------------------------------------------------
// Level goals definition (manual overrides only)
// If a level is NOT defined here, we generate defaults.
// -----------------------------------------------------
export const ZC_LEVEL_GOALS = {
  // =========================
  // 🔥 Mundo Aries (tutorial overrides)
  // =========================
  // NOTE: targets for score/clears are auto-calibrated by moves.
  // Put explicit numbers only if you really want a fixed value.
  1: [{ type: 'score' }],
  2: [{ type: 'stripesActivated', target: 2 }],
  3: [{ type: 'clear_element_fire' }],
  4: [{ type: 'starsCreated', target: 1 }],
  5: [{ type: 'score', target: 750 }],
};

function inferClearTypeFromWorld(level) {
  const el = getWorldElementForLevel(level);
  if (el === 'fire') return 'clear_element_fire';
  if (el === 'water') return 'clear_element_water';
  if (el === 'air') return 'clear_element_air';
  if (el === 'earth') return 'clear_element_earth';
  return null;
}

function generateDefaultGoalsForLevel(level, signKey = null, worldIdentityVersion = 1) {
  const lv = normalizeAbsoluteLevel(level);
  const inWorld = getIndexInWorld(lv);

  // Pattern inside each world:
  // 1: score
  // 2: stripes
  // 3: clear world element
  // 4: star
  // 5: cascades
  // 6: score
  // 7: stripes
  // 8: clear
  // 9: score
  // 10: stripes
  // 11: cascades
  // 12: score
  // 13: clear
  // 14: stripes
  // 15: score
  // 16: cascades
  // 17: clear
  // 18: stripes
  // 19: score
  // 20 (boss): score + stripes + star

  const clearType = inferClearTypeFromWorld(lv);

  const mkScore = () => ({ type: 'score' });
  const mkStripes = () => ({ type: 'stripesActivated', target: clamp(2 + Math.floor((inWorld - 1) / 6), 2, 8) });
  const mkStar = () => ({ type: 'starsCreated', target: clamp(1 + (inWorld >= 15 ? 1 : 0), 1, 3) });
  const mkCascades = () => ({ type: 'cascades', target: clamp(1 + Math.floor((inWorld - 1) / 7), 1, 6) });
  const mkClear = () => (clearType ? ({ type: clearType }) : mkScore());

  if (inWorld === 20) {
  // Boss-ish level: keep the triple objective for a bigger finish.
  return [mkScore(), mkStripes(), mkStar()];
}

  const slot = ((inWorld - 1) % 6) + 1;
// ✅ Default: require SCORE + one secondary objective.
const primary = mkScore();

let secondary;
  if (Number(worldIdentityVersion) === 2) {
    const family = getWorldGoalFamily(getWorldElementForLevel(lv, signKey));
    const flavor = getWorldSignFlavor(signKey);
    if (flavor === 'deep_cascade' || family.includes('cascades')) secondary = mkCascades();
    else if (flavor === 'specials' || family.includes('specials') || family.includes('directional_specials')) secondary = mkStripes();
    else if (flavor === 'precision' || flavor === 'efficiency' || family.includes('controlled_clear')) secondary = mkClear();
    else if (family.includes('clear_element') || family.includes('controlled_clear')) secondary = mkClear();
    else if (family.includes('combos') || family.includes('multi_element')) secondary = slot % 2 ? mkStripes() : mkStar();
  } else if (slot === 1) secondary = mkClear();
  else if (slot === 2) secondary = mkStripes();
else if (slot === 3) secondary = mkStar();
else if (slot === 4) secondary = mkCascades();
else if (slot === 5) secondary = mkClear();
else secondary = mkStripes();

// Avoid duplicates if a fallback produced the same type.
if (!secondary || secondary.type === primary.type) return [primary];

return [primary, secondary];
}

function calibrateTargetsForLevel(level, goals) {
  const lv = normalizeAbsoluteLevel(level);
  const list = Array.isArray(goals) ? goals : [];

  const scoreTarget = scoreTargetForLevel(lv);
  const clearTarget = clearsTargetForLevel(lv);

  return list.map((g) => {
    const type = normalizeGoalType(g?.type);

    // If user provided an explicit target, keep it.
    const hasExplicitTarget = Number.isFinite(Number(g?.target)) && Number(g.target) > 0;

    if (type === 'score') {
      return { ...g, type, target: hasExplicitTarget ? Number(g.target) : scoreTarget };
    }

    if (
      type === 'clear_element_fire' ||
      type === 'clear_element_water' ||
      type === 'clear_element_air' ||
      type === 'clear_element_earth'
    ) {
      return { ...g, type, target: hasExplicitTarget ? Number(g.target) : clearTarget };
    }

    // Other types: keep given target or default to 1
    if (!hasExplicitTarget) {
      return { ...g, type, target: 1 };
    }

    return { ...g, type, target: Number(g.target) };
  });
}

// -----------------------------------------------------
// Public API
// -----------------------------------------------------

/**
 * Returns a NORMALIZED goals array for a level.
 * Each goal gets: { id, type, target, label, icon }
 */
export function getGoalsForLevel({ level, signKey, worldIdentityVersion = 1 } = {}) {
  const lvl = normalizeAbsoluteLevel(level, signKey);
    // -----------------------------------------------------
  // Cross-world overrides by *index in world* (1..20)
  // Si querés que ciertos niveles sean iguales en TODOS los mundos,
  // definilos acá por inWorld.
  // -----------------------------------------------------
  const inWorld = getIndexInWorld(lvl, signKey);

  // Prefer backend map objectives from cache when available.
  // This ensures the in-level goals match what `/map/levels` returns.
  const cachedObjectives = tryGetCachedMapObjectives(lvl);
  const hasCachedObjectives = Array.isArray(cachedObjectives) && cachedObjectives.length > 0;

  // Manual override OR cached backend objectives OR generated defaults
  const raw = hasCachedObjectives
    ? cachedObjectives
    : (ZC_LEVEL_GOALS[lvl] || (inWorld === 5 ? [{ type: 'score', target: 750 }] : generateDefaultGoalsForLevel(lvl, signKey, worldIdentityVersion)));

  const baseGoals = Array.isArray(raw) ? raw : [];

  // Auto-calibrate score/clear targets by moves bucket ONLY when targets are not explicit.
  // (calibrateTargetsForLevel already respects explicit targets)
  const goals = calibrateTargetsForLevel(lvl, baseGoals);

  // Normalize + ensure stable ids
  return goals
    .map((g, idx) => {
      const type = normalizeGoalType(g?.type);
      const target = Math.max(1, Number(g?.target || 0) || 1);
      const meta = GOAL_META[type] || { label: 'Objetivo', icon: '🎯', mode: 'delta' };

      // Stable id per goal slot
      const base = `${lvl}_${type}_${idx}`;
      const suffix = signKey ? `_${String(signKey)}` : '';
      const id = String(g?.id || `${base}${suffix}`);

      return {
        id,
        type,
        target,
        label: String(g?.label || meta.label || 'Objetivo'),
        icon: String(g?.icon || meta.icon || '🎯'),
      };
    })
    .filter((g) => !!g.type && Number.isFinite(g.target));
}

/**
 * Inicializa progreso del nivel.
 * Acepta:
 *  - goals array normalizado (recomendado)
 *  - o un `level` number (compat con versiones previas)
 */
export function initLevelProgress(input, opts = {}) {
  // Back-compat: if input is a number, treat it as level
  let goals = input;
  if (typeof input === 'number' || typeof input === 'string') {
    goals = getGoalsForLevel({ level: Number(input) || 1, signKey: opts?.signKey });
  }

  const list = Array.isArray(goals) ? goals : [];
  const bestScore = Number(opts?.bestScore ?? 0) || 0;

  return list.map((g, idx) => {
    const type = normalizeGoalType(g?.type);
    const targetRaw = Number(g?.target);
    const target = (Number.isFinite(targetRaw) && targetRaw > 0) ? targetRaw : 1;

    // Persisted completion (only score is globally persistent today)
    const meta = GOAL_META[type] || { mode: 'delta' };
    const persistedValue = (meta.mode === 'absolute' && type === 'score')
      ? Math.max(0, bestScore)
      : 0;

    const initialValue = Math.min(persistedValue, target);
    const initialDone = initialValue >= target;

    return {
      id: String(g?.id || `goal_${type || 'unknown'}_${idx}`),
      type,
      label: String(g?.label || formatGoalLabel(g)),
      icon: String(g?.icon || formatGoalIcon(g)),
      target,
      value: initialValue,
      done: initialDone,
    };
  });
}
// -----------------------------------------------------
// Persistence helpers
// -----------------------------------------------------

/**
 * Applies persisted completion to an existing progress array.
 * Today, only SCORE goals are truly persistent (bestScore per level).
 * This is UI-safe and side-effect free.
 */
export function applyPersistedLevelProgress(progress, opts = {}) {
  const list = Array.isArray(progress) ? progress : [];
  const bestScore = Number(opts?.bestScore ?? 0) || 0;

  return list.map((g) => {
    const type = normalizeGoalType(g?.type);
    const targetRaw = Number(g?.target);
    const target = (Number.isFinite(targetRaw) && targetRaw > 0) ? targetRaw : 1;
    const meta = GOAL_META[type] || { mode: 'delta' };

    if (meta.mode === 'absolute' && type === 'score') {
      const v = Math.max(Number(g?.value || 0) || 0, bestScore);
      const done = v >= target;
      return {
        ...g,
        value: Math.min(v, target),
        done: !!g?.done || done,
      };
    }

    return g;
  });
}

/**
 * Aplica progreso luego de un movimiento.
 * Inputs:
 * - progress: array de progreso
 * - moveAgg: moveMissionAgg (tu agregado por movimiento)
 * - score: score acumulado ACTUAL
 *
 * Output:
 * {
 *   nextProgress,
 *   justCompleted,   // array de goal IDs recién completados
 *   doneCount,
 *   total,
 * }
 */
export function applyLevelProgress(progress, moveAgg, score) {
  if (!Array.isArray(progress)) {
    return {
      nextProgress: [],
      justCompleted: [],
      doneCount: 0,
      total: 0,
    };
  }

  const justCompleted = [];

  const nextProgress = progress.map((g) => {
    const type = normalizeGoalType(g?.type);
    const meta = GOAL_META[type] || { mode: 'delta' };

    // Keep already-completed goals stable
    if (g?.done) {
      const v = Number(g?.value || 0) || 0;
      const t = Number(g?.target || 0) || 0;
      return { ...g, value: Math.min(v, t), done: true };
    }

    const targetRaw = Number(g?.target);
    const target = (Number.isFinite(targetRaw) && targetRaw > 0) ? targetRaw : 1;
    let nextValue = Number(g?.value || 0) || 0;

    if (meta.mode === 'absolute') {
      if (type === 'score') {
        // Score goal uses the absolute score
        nextValue = Math.max(nextValue, Number(score || 0) || 0);
      } else {
        // Other absolute goals read their absolute value from moveAgg
        // (ex: cascades can be tracked as max cascade depth in the current run)
        const keys = Array.isArray(meta.absKeys) ? meta.absKeys : [];
        let absV = 0;
        for (const kk of keys) {
          const v = Number(moveAgg?.[kk] || 0) || 0;
          if (v > absV) absV = v;
        }
        nextValue = Math.max(nextValue, absV);
      }
    } else {
      // Delta-based goals are accumulated per move.
      // `moveAgg` is a per-move/per-batch delta, not an absolute run snapshot.
      const k = meta.deltaKey;
      const snap = k ? (Number(moveAgg?.[k] || 0) || 0) : 0;
      nextValue = nextValue + snap;
    }

    const done = nextValue >= target;

    if (done && !g?.done) {
      justCompleted.push(String(g?.id || g?.type || 'goal'));
    }

    return {
      ...g,
      value: Math.min(nextValue, target),
      done,
    };
  });

  const doneCount = nextProgress.filter((x) => !!x?.done).length;

  return {
    nextProgress,
    justCompleted,
    doneCount,
    total: nextProgress.length,
  };
}

// -----------------------------------------------------
// Helpers UI-friendly
// -----------------------------------------------------
export function formatGoalLabel(goal) {
  const type = normalizeGoalType(goal?.type);
  return GOAL_META[type]?.label || 'Objetivo';
}

export function formatGoalIcon(goal) {
  const type = normalizeGoalType(goal?.type);
  return GOAL_META[type]?.icon || '🎯';
}

/**
 * Returns a compact UI text like:
 * "🎯 Sumá puntos 120/250" or "⚡ Activá rayos 1/2"
 */
export function formatGoalProgressText(goalProgressItem) {
  if (!goalProgressItem) return '';
  const label = String(goalProgressItem.label || formatGoalLabel(goalProgressItem));
  const icon = String(goalProgressItem.icon || formatGoalIcon(goalProgressItem));
  const v = Math.max(0, Number(goalProgressItem.value || 0) || 0);
  const t = Math.max(0, Number(goalProgressItem.target || 0) || 0);
  return `${icon} ${label} ${v}/${t}`;
}

/**
 * Simple completion fraction string: "1/2".
 */
export function goalsDoneStats(progress) {
  const list = Array.isArray(progress) ? progress : [];
  const done = list.filter((g) => !!g?.done).length;
  return { done, total: list.length || 0 };
}
